import { AutocompleteCommissions } from '@components/AutoComplete/AutocompleteCommissions';
import { AutocompleteWowEffect } from '@components/AutoComplete/AutocompleteWowEffect';
import CustomSwitch from '@components/CustomSwitch';
import { FlexRow } from '@components/FlexRow';
import FormSection from '@components/FormSection';
import InputDate from '@components/InputDate';
import InputText from '@components/InputFieldControlled';
import InputMultiSelect from '@components/InputMultiSelect';
import InputSwitchControl from '@components/InputSwitchControl';
import Text from '@components/Text';
import Colors from '@constants/Colors';
import { useOptionsContext } from '@modules/talents/context/options-context';
import { Card, FormLabel, Grid, Stack, Typography, useMediaQuery } from '@mui/material';
import { moment } from '@utils/date.utils';
import NumberUtils from '@utils/number.utils';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

const MIN_HOURS = 35;
const WEEKS = 52;
const MONTHS = 12;

const getMinSalary = (workHours, hourlyRate) => {
    if (workHours > MIN_HOURS) {
        const minSalary = ((MIN_HOURS * WEEKS) / MONTHS) * hourlyRate;
        const extraHours = workHours - MIN_HOURS;
        const extraSalary = ((extraHours * WEEKS) / MONTHS) * hourlyRate * 1.25;
        return Math.ceil(minSalary + extraSalary);
    }

    return Math.ceil(((workHours * WEEKS) / MONTHS) * hourlyRate);
};

const ContractForm = () => {
    const { data: optionsData } = useOptionsContext();
    const options = useMemo(() => optionsData?.options, [optionsData]);
    const { control, getValues, setValue, watch, clearErrors } = useFormContext();
    const values = getValues();
    const [salary, setSalary] = useState(null);
    const [rtt, setRtt] = useState(values?.contract?.rtt?.isActive);

    const [workHours, setWorkHours] = useState(35);
    const [contractType, setContractType] = useState(values?.contract?.contractType);
    const [contractStatus, setContractStatus] = useState(values?.contract?.status);
    const [bonus, setBonus] = useState(
        // @ts-ignore
        getValues('contract.remuneration.max') -
        (getValues('contract.guaranteedSalary.max')
            ? getValues('contract.guaranteedSalary.max')
            : getValues('contract.guaranteedSalary.min')) ?? 0,
    );
    const isFreelance = contractStatus === 'Indépendant / freelance';

    // check if contract Alternance or Stage, the salary is not required
    const isSalaryRequired = !['Alternance (apprentissage et autres)', 'Stage'].includes(contractType);

    const isPartTime = contractType === 'CDI temps partiel' || contractType === 'CDD temps partiel';
    const isFullTime = contractType === 'CDI' || contractType === 'CDD';

    const isDateRequired = !['CDI temps partiel', 'CDI'].includes(contractType);

    const publishDate = watch('publishDate');
    const enablePublishDate = watch('enablePublishDate');
    const isAnnualSalary = watch('contract.isAnnualSalary');

    useEffect(() => {
        if (contractStatus) {
            if (isFreelance) {
                setValue('contract.contractType', 'Freelance');
                setValue('contract.remuneration.min', null);
                setValue('contract.guaranteedSalary.min', null);
                setValue('contract.guaranteedSalary.max', null);
            } else {
                if (contractType === 'Freelance') {
                    setValue('contract.contractType', null);
                }
            }
        }
    }, [isFreelance, contractStatus, contractType, setValue]);

    const isMobile = useMediaQuery('(max-width:600px)');

    const validateMinSalary = useCallback(
        val => {
            if (!isSalaryRequired && !val) return null;
            if (!val) return 'Salaire min requis';
            if (isFreelance) return null;
            const minSalary = Math.round(getMinSalary(workHours, options?.minHourlyRate));
            const salary = parseInt(val);
            if (isAnnualSalary) {
                return salary < minSalary * 12
                    ? `Salaire min ${NumberUtils.currencyFormatWithoutZero(minSalary * 12)}/an`
                    : null;
            }
            return salary < minSalary ? `Salaire min ${NumberUtils.currencyFormatWithoutZero(minSalary)}/mois` : null;
        },
        [workHours, options?.minHourlyRate, isFreelance, isSalaryRequired, isAnnualSalary],
    );

    const validateMaxSalary = useCallback(
        val => {
            if (!val) return null;
            if (isFreelance) return null;
            const minSalary = Math.round(getMinSalary(workHours, options?.minHourlyRate));
            const salary = parseInt(val);
            const isAnnualSalary = watch('contract.isAnnualSalary');
            if (isAnnualSalary) {
                return salary < minSalary * 12
                    ? `Salaire min ${NumberUtils.currencyFormatWithoutZero(minSalary * 12)}/an`
                    : null;
            }
            return salary < minSalary ? `Salaire min ${NumberUtils.currencyFormatWithoutZero(minSalary)}/mois` : null;
        },
        [isFreelance, workHours, options?.minHourlyRate, watch],
    );

    return (
        <>
            <FormSection
                title="Contrat"
                description="Partagez ici, les informations sur le contrat, sa durée, son volume horaire, etc"
                showTitle>
                <InputMultiSelect
                    single
                    label="Contrat"
                    placeholder="Ex: CDI"
                    items={
                        options?.preferences
                            ? [
                                {
                                    ...options?.preferences?.[0],
                                    children: options?.preferences?.[0]?.children?.filter(item => {
                                        if (!contractStatus) return true;
                                        return isFreelance
                                            ? item.name === 'Freelance'
                                            : item.name !== 'Freelance';
                                    }),
                                },
                            ]
                            : []
                    }
                    control={control}
                    name="contract.contractType"
                    defaultValue={values?.contract?.contractType}
                    onChange={val => {
                        setContractType(val);
                    }}
                    required
                />
                <InputMultiSelect
                    single
                    label="Statut"
                    placeholder="Ex: Cadre"
                    items={options?.contractStatues}
                    control={control}
                    name="contract.status"
                    defaultValue={values?.contract?.status}
                    required
                    onChange={val => {
                        setContractStatus(val);
                        if (['CDI temps partiel', 'CDI'].includes(contractType)) {
                            setValue('contract.endDate', null);
                        }
                    }}
                />
                <FlexRow alignItems={'flex-start'}>
                    <InputDate
                        name="contract.startDate"
                        control={control}
                        label="Date de début"
                        placeholder="-- / -- / ----"
                        minDate={moment(publishDate ?? new Date())}
                        maxDate={watch('contract.endDate')}
                        showClear
                        defaultValue={values?.contract?.startDate}
                        rules={{
                            minDate: val => {
                                if (!isEmpty(val) && !!publishDate && enablePublishDate) {
                                    const minDate = moment(publishDate).add(1, 'days');
                                    return moment(val).isSameOrAfter(minDate)
                                        ? true
                                        : `Date de début doit être supérieure à ${minDate.format(
                                            'DD/MM/YYYY',
                                        )} (date de publication de l'offre)
                                            }`;
                                }
                                return true;
                            },
                        }}
                    />
                    {isDateRequired && (
                        <>
                            <InputDate
                                name="contract.endDate"
                                control={control}
                                label="Date de fin "
                                showClear
                                minDate={watch('contract.startDate')}
                                placeholder="-- / -- / ----"
                                defaultValue={values?.contract?.endDate}
                            />
                            <Typography variant="caption" color={Colors.NEUTRAL_DARK_GRAY} mt={0.5}>
                                Laisser vide si le contrat est à durée indéterminée (CDI)
                            </Typography>
                        </>
                    )}
                </FlexRow>
                <div>
                    <Stack flexDirection={'row'} alignItems="center" gap={2} mb={1} mt={2} justifyContent={"space-between"}>
                        <FormLabel>Type de rémunération</FormLabel>
                        <CustomSwitch
                            onChange={val => {
                                setValue('contract.rateType', val);
                            }}
                            options={[
                                {
                                    value: 'hourly',
                                    label: 'Forfait Heures',
                                },
                                {
                                    value: 'daily',
                                    label: 'Forfait Jours',
                                },
                            ]}
                            value={values?.contract?.rateType}
                            style={{
                                containerStyle: {
                                    height: '48px',
                                },
                            }}
                        />
                    </Stack>

                    {watch('contract.rateType') === 'hourly' && (
                        <InputText
                            name="contract.hourPerWeek"
                            control={control}
                            label="Durée hebdomadaire"
                            placeholder="ex : 35 Heures"
                            required
                            rules={{
                                minMaxContractType: val => {
                                    if (isPartTime) {
                                        return (
                                            Number(val) < 35 ||
                                            `Merci de sélectionner un contrat en temps plein (CDI/CDD) pour une durée supérieure ou égale à 35 heures par semaine`
                                        );
                                    }
                                    if (isFullTime) {
                                        return (
                                            Number(val) >= 35 ||
                                            `Merci de sélectionner un contrat en temps partiel pour une durée inférieure à 35 heures par semaine`
                                        );
                                    }
                                    return true;
                                },
                            }}
                            defaultValue={values?.contract?.hourPerWeek?.toString()}
                            onChange={val => {
                                setWorkHours(val);
                            }}
                        />
                    )}
                </div>
                <InputMultiSelect
                    label="Nombre de jours de travail hebdomadaire"
                    placeholder="Maximum 6 jours"
                    items={[
                        {
                            name: 'Ajouter un jour de travail',
                            id: 'jours',
                            children: [
                                { name: '1', id: 1 },
                                { name: '1.5', id: 1.5 },
                                { name: '2', id: 2 },
                                { name: '2.5', id: 2.5 },
                                { name: '3', id: 3 },
                                { name: '3.5', id: 3.5 },
                                { name: '4', id: 4 },
                                { name: '4.5', id: 4.5 },
                                { name: '5', id: 5 },
                                { name: '5.5', id: 5.5 },
                                { name: '6', id: 6 },
                            ],
                        },
                    ]}
                    name="contract.dayPerWeek"
                    control={control}
                    defaultValue={values?.contract?.dayPerWeek}
                />
            </FormSection>

            <FormSection
                title="Salaire"
                description="Plus vous détaillerez la composition du salaire et plus le talent pourra se projeter"
                showTitle>
                {!isFreelance && (
                    <InputText
                        name="contract.monthPerYear"
                        control={control}
                        label="Nombre de mois de salaire (minimum 12/an)"
                        placeholder="ex : 14"
                        keyboardType="number-pad"
                        rules={{
                            min: val => {
                                return val < 12 ? `Minimum 12` : null;
                            },
                        }}
                        defaultValue={values?.contract?.monthPerYear?.toString()}
                        required
                    />
                )}
                {!isFreelance && (
                    <Grid container justifyContent="flex-end" spacing={1}>
                        <Grid item width={isMobile ? 130 : undefined} xs={isMobile ? undefined : 6}>
                            <Stack justifyContent={'flex-end'} style={{ height: '100%' }}>
                                <Text>Congés payés (nombre)</Text>
                                <div>
                                    <InputText
                                        name="contract.dayOff.number"
                                        control={control}
                                        placeholder="ex : 25"
                                        keyboardType="number-pad"
                                        defaultValue={values?.contract?.dayOff?.number?.toString()}
                                        required
                                    />
                                </div>
                            </Stack>
                        </Grid>
                        <Grid item flex={isMobile ? 1 : undefined} xs={isMobile ? undefined : 6}>
                            <Stack justifyContent={'flex-end'} style={{ height: '100%' }}>
                                <Text>Congés payés (unité)</Text>
                                <InputMultiSelect
                                    name={'contract.dayOff.unit'}
                                    control={control}
                                    single
                                    defaultValue={values?.contract?.dayOff?.unit}
                                    placeholder="ex : jours"
                                    required
                                    options={[
                                        {
                                            label: 'Jours',
                                            value: 'Jours',
                                        },
                                        {
                                            label: 'Semaine',
                                            value: 'Semaine',
                                        },
                                    ]}
                                />
                            </Stack>
                        </Grid>
                    </Grid>
                )}
                {!isFreelance && (
                    <div>
                        <InputSwitchControl
                            control={control}
                            name="contract.rtt.isActive"
                            defaultValue={values?.contract?.rtt?.isActive}
                            title="RTT"
                            style={{ alignItems: 'center' }}
                            onChange={val => {
                                setRtt(val);
                            }}
                        />
                        {rtt && (
                            <InputText
                                name="contract.rtt.days"
                                control={control}
                                label="Nombre de jours"
                                keyboardType="number-pad"
                                placeholder={rtt ? 'ex : 5' : '0'}
                                rules={{
                                    requiredIf: val => {
                                        if (!rtt) return true;

                                        return !!val ? true : 'Nombre de jours requis si RTT est activé';
                                    },
                                }}
                                defaultValue={values?.contract?.rtt?.days?.toString()}
                            />
                        )}
                    </div>
                )}
                {!isFreelance && (
                    <>
                        <InputSwitchControl
                            control={control}
                            name="contract.showSalary"
                            defaultValue={values?.contract?.showSalary}
                            title="Afficher le salaire dans l'offre"
                            style={{ alignItems: 'center' }}
                            containerStyle={{
                                marginBottom: 2,
                            }}
                        />
                        <InputSwitchControl
                            control={control}
                            name="contract.isAnnualSalary"
                            defaultValue={values?.contract?.isAnnualSalary}
                            title="Exprimer la rémunération en montant brut annuel"
                            style={{ alignItems: 'center' }}
                            containerStyle={{
                                marginBottom: 2,
                            }}
                            onChange={(annual: boolean) => {
                                const monthlyMinimum = +getValues('contract.guaranteedSalary.min');
                                const monthlyMaximum = +getValues('contract.guaranteedSalary.max');
                                const remunerationMin = +getValues('contract.remuneration.min');
                                const perYear = +getValues('contract.monthPerYear');
                                const commission = Math.round(+getValues('contract.commissions'))
                                    ? +getValues('contract.commissions')
                                    : remunerationMin - monthlyMinimum;

                                if (annual) {
                                    setValue('contract.commissions', +(commission * 12).toFixed(2));
                                    setValue(
                                        'contract.guaranteedSalary.min',
                                        +(monthlyMinimum * perYear).toFixed(2),
                                    );
                                    setValue(
                                        'contract.guaranteedSalary.max',
                                        +(monthlyMaximum * perYear).toFixed(2),
                                    );
                                    setValue('contract.remuneration.min', +(monthlyMinimum * perYear).toFixed(2));
                                    setValue(
                                        'contract.remuneration.max',
                                        +(monthlyMaximum * perYear + commission * 12).toFixed(2),
                                    );
                                } else {
                                    setValue('contract.commissions', +(commission / 12).toFixed(2));
                                    setValue(
                                        'contract.guaranteedSalary.min',
                                        +(monthlyMinimum / perYear).toFixed(2),
                                    );
                                    setValue(
                                        'contract.guaranteedSalary.max',
                                        +(monthlyMaximum / perYear).toFixed(2),
                                    );
                                    setValue('contract.remuneration.min', +(monthlyMinimum / perYear).toFixed(2));
                                    setValue(
                                        'contract.remuneration.max',
                                        +(monthlyMaximum / perYear + commission / 12).toFixed(2),
                                    );
                                }
                            }}
                        />
                        <FlexRow gap={1} alignItems="flex-start">
                            <InputText
                                name={`contract.guaranteedSalary.min`}
                                control={control}
                                label="Salaire Fixe Mini"
                                placeholder={`ex : 2000`}
                                keyboardType="number-pad"
                                iconRight={
                                    <Text darkGray>{watch('contract.isAnnualSalary') ? ' €/an' : ' €/mois'}</Text>
                                }
                                iconSize={16}
                                iconRightStyle={{ color: Colors.PRIMARY }}
                                defaultValue={values?.contract?.guaranteedSalary?.min?.toString()}
                                showErrorMessage={!!salary}
                                rules={{
                                    min: validateMinSalary,
                                }}
                                onChange={val => {
                                    setSalary(val);
                                    clearErrors('contract.remuneration.min');
                                    setValue('contract.remuneration.min', +val);
                                    if (!getValues('contract.guaranteedSalary.max')) {
                                        setValue('contract.remuneration.max', +val + bonus);
                                    }
                                }}
                            />
                            <InputText
                                name={`contract.guaranteedSalary.max`}
                                control={control}
                                label="Salaire Fixe Maxi"
                                placeholder={`ex : 2 500`}
                                keyboardType="number-pad"
                                iconRight={
                                    <Text darkGray>{watch('contract.isAnnualSalary') ? ' €/an' : ' €/mois'}</Text>
                                }
                                iconSize={16}
                                iconRightStyle={{ color: Colors.PRIMARY }}
                                defaultValue={values?.contract?.guaranteedSalary?.max?.toString()}
                                showErrorMessage={!!salary}
                                rules={{
                                    min: validateMaxSalary,
                                }}
                                onChange={val => {
                                    if (+val > +getValues('contract.guaranteedSalary.min')) {
                                        setValue('contract.remuneration.max', +val + bonus);
                                    }
                                    if (
                                        !val ||
                                        +getValues('contract.guaranteedSalary.max') <
                                        +getValues('contract.guaranteedSalary.min')
                                    ) {
                                        setValue(
                                            'contract.remuneration.max',
                                            +getValues('contract.guaranteedSalary.min') + bonus,
                                        );
                                    }
                                }}
                            />
                        </FlexRow>
                    </>
                )}
                <Grid item flex={1}>
                    <Stack justifyContent={'flex-end'} style={{ height: '100%' }}>
                        <InputSwitchControl
                            control={control}
                            name="contract.showBonus"
                            defaultValue={values?.contract?.showBonus}
                            title="Afficher le détail des commissions"
                            style={{ alignItems: 'center' }}
                            containerStyle={{
                                marginBottom: 2,
                            }}
                        />
                        <InputText
                            name={`contract.commissions`}
                            control={control}
                            label={'Commissions & Primes (jusqu’à)'}
                            placeholder={`ex : 2 500`}
                            keyboardType="number-pad"
                            iconRight={
                                <Text darkGray>{watch('contract.isAnnualSalary') ? ' €/an' : ' €/mois'}</Text>
                            }
                            iconSize={16}
                            iconRightStyle={{ color: Colors.PRIMARY }}
                            defaultValue={(values?.contract?.remuneration?.max -
                                (values?.contract?.guaranteedSalary?.max
                                    ? values?.contract?.guaranteedSalary?.max
                                    : values?.contract?.guaranteedSalary?.min)
                            ).toString()}
                            showErrorMessage={!!salary}
                            onChange={val => {
                                setBonus(+val);
                                setValue('contract.commissions', +val);
                                setValue(
                                    'contract.remuneration.max',
                                    (getValues('contract.guaranteedSalary.max')
                                        ? +getValues('contract.guaranteedSalary.max')
                                        : +getValues('contract.guaranteedSalary.min')) + (Number(val) || 0),
                                );
                            }}
                        />
                    </Stack>
                </Grid>
                <Grid item flex={1}>
                    <Stack justifyContent={'flex-end'} style={{ height: '100%' }}>
                        <AutocompleteCommissions
                            label="Calculée sur"
                            placeholder=" Ex : Chiffre d'affaires"
                            groupedOptions={options?.commissions}
                            defaultValue={values?.contract?.bonus}
                            name={`contract.bonus`}
                            control={control}
                            allowAdd
                            multiple={false}
                        />
                    </Stack>
                </Grid>

                <FlexRow gap={1} alignItems="flex-start">
                    <InputText
                        name={`contract.remuneration.min`}
                        control={control}
                        label="Rémunération Mini"
                        placeholder={`ex : 2000`}
                        keyboardType="number-pad"
                        disabled
                        iconRight={<Text darkGray>{watch('contract.isAnnualSalary') ? ' €/an' : ' €/mois'}</Text>}
                        iconSize={16}
                        iconRightStyle={{ color: Colors.PRIMARY }}
                        defaultValue={(values?.contract?.remuneration?.min * 12).toString()}
                        showErrorMessage={!!salary}
                        onChange={val => {
                            setSalary(val);
                            clearErrors('contract.remuneration.min');
                            setValue('contract.remuneration.min', val);
                        }}
                    />
                    <InputText
                        name={`contract.remuneration.max`}
                        control={control}
                        label="Rémunération Maxi"
                        placeholder={`ex : 2 500s`}
                        keyboardType="number-pad"
                        disabled
                        iconRight={<Text darkGray>{watch('contract.isAnnualSalary') ? ' €/an' : ' €/mois'}</Text>}
                        iconSize={16}
                        iconRightStyle={{ color: Colors.PRIMARY }}
                        defaultValue={(values?.contract?.remuneration?.max * 12).toString()}
                        showErrorMessage={!!salary}
                    />
                </FlexRow>
                {isFreelance && (
                    <InputSwitchControl
                        control={control}
                        name="contract.isAnnualSalary"
                        defaultValue={values?.contract?.isAnnualSalary}
                        title="Exprimer la rémunération en montant brut annuel"
                        style={{ alignItems: 'center' }}
                        containerStyle={{
                            marginBottom: 2,
                        }}
                    />
                )}
            </FormSection>
        </>
    );
};

export default ContractForm;
